import React, { useState, useRef, useEffect, type FC } from "react";

import { MaxWidthWrapper } from "@/components/maxWidthWrapper";
import { PlayIcon } from "lucide-react";

const POSTER_FRAME_URL =
  "https://zorts-public-image-assets.s3.amazonaws.com/flash-web/explainer-anim.png";
const VIDEO_URL =
  "https://zorts-public-image-assets.s3.us-east-1.amazonaws.com/flash-web/explainer-anim.mp4";

export const FfrVideo: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handlePlayClick = (): void => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    videoElement.addEventListener("pause", handlePause);
    videoElement.addEventListener("ended", handleEnded);

    return () => {
      videoElement.removeEventListener("pause", handlePause);
      videoElement.removeEventListener("ended", handleEnded);
    };
  }, []);

  return (
    <MaxWidthWrapper>
      <div className="relative grid min-h-[200px] place-items-center py-16">
        <Content ffrVideoRef={videoRef} isPlaying={isPlaying} />
        {!isPlaying && <PlayButton onClick={handlePlayClick} />}
      </div>
    </MaxWidthWrapper>
  );
};

interface PlayButtonProps {
  onClick: () => void;
}

const PlayButton: FC<PlayButtonProps> = ({ onClick }) => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
    <div className="group relative w-fit transition-transform duration-200 active:scale-95">
      <button
        onClick={onClick}
        className="relative z-10 rounded-xl bg-gradient-to-br from-emerald-500 via-green-900 to-teal-200 p-0.5 duration-200 group-hover:scale-110"
      >
        <span className="block rounded-xl bg-zinc-950 px-4 py-2 font-semibold text-zinc-100 duration-200 group-hover:bg-zinc-950/80 group-hover:text-zinc-50">
          <PlayIcon className="size-8" />
        </span>
      </button>
      <span className="pointer-events-none absolute -inset-4 z-0 transform-gpu rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-200 opacity-30 blur-xl transition-all duration-200 group-hover:opacity-90 group-active:opacity-50" />
    </div>
  </div>
);

interface ContentProps {
  ffrVideoRef: React.RefObject<HTMLVideoElement | null>;
  isPlaying: boolean;
}

const Content: FC<ContentProps> = ({ ffrVideoRef, isPlaying }) => (
  <div className="not-sr-only flex w-full items-center justify-center">
    <video
      ref={ffrVideoRef}
      aria-hidden="true"
      playsInline={true}
      controls={isPlaying}
      preload="auto"
      poster={POSTER_FRAME_URL}
      className="inset-0 aspect-video rounded-lg object-cover md:rounded-[32px]"
    >
      <source src={VIDEO_URL} type="video/mp4" />
    </video>
  </div>
);
